/**
 * Hero — Landing, Códice Botánico edition.
 *
 * Mexican botanical-apothecary hero: amate-paper field, a fret crest, the
 * colibrí messenger, and a live phone demo — the REAL app embedded in a
 * CSS phone frame (iframe to /dashboard, which opens in guest mode).
 * Navigation, analytics (trackSignup), and the demo-scroll behaviour are
 * unchanged.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { trackSignup } from '../../utils/analytics';

const TAGS = ['Expediente clínico', 'Planes SMAE', 'Seguimiento de progreso', 'Escáner NOM-051', 'Plantas UNAM', 'Cocina inteligente'];

const Hero = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    trackSignup('landing_hero');
    navigate('/auth/register');
  };

  const handleWatchDemo = () => {
    const demoSection = document.getElementById('demo-video');
    if (demoSection) demoSection.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="cdx-grain relative min-h-screen flex items-center overflow-hidden"
      style={{
        background:
          'radial-gradient(120% 90% at 85% 10%, #ECDDC4 0%, #F4EBD9 45%, #F4EBD9 100%)',
        fontFamily: 'var(--cdx-font-body)',
      }}
    >
      {/* Fret crest top band */}
      <div className="cdx-greca absolute top-0 left-0 right-0 z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full relative z-[1]">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* ---- Left column ---- */}
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="cdx-kicker">Plataforma de nutrición · Nutriólogos y pacientes · 100% gratis</span>

            <h1
              className="cdx-display mt-3 mb-6"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', lineHeight: 1.04, color: 'var(--cdx-ink)' }}
            >
              Nutrición profesional,
              <br />
              <span style={{ color: 'var(--cdx-jade)', fontStyle: 'italic' }}>
                seguimiento real
              </span>{' '}
              para cada paciente.
            </h1>

            <p className="text-lg mb-7" style={{ color: 'var(--cdx-ink-soft)', lineHeight: 1.65, maxWidth: 540 }}>
              <strong style={{ color: 'var(--cdx-ink)' }}>Para nutriólogos:</strong> expediente clínico,
              planes de alimentación SMAE y seguimiento de progreso en un solo lugar.{' '}
              <strong style={{ color: 'var(--cdx-ink)' }}>Para pacientes:</strong> tu plan, tu avance y
              Huitzi, tu guía con IA — con el respaldo de las plantas medicinales mexicanas.
            </p>

            {/* Labor social ribbon */}
            <div
              className="mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2"
              style={{
                background: 'linear-gradient(160deg, var(--cdx-jade-bright), var(--cdx-jade))',
                color: 'var(--cdx-nighttext)',
                boxShadow: 'var(--cdx-shadow-md)',
              }}
            >
              <span className="text-lg">🌱</span>
              <span className="text-sm font-bold">100% gratis — labor social de Scram</span>
            </div>

            {/* Botanical module pills */}
            <div className="mb-8 flex flex-wrap gap-2">
              {TAGS.map((tag) => (
                <span key={tag} className="cdx-tag">{tag}</span>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={handleGetStarted} className="cdx-btn cdx-btn--primary" style={{ padding: '15px 30px', fontSize: 17 }}>
                Empezar gratis
              </button>
              <button onClick={handleWatchDemo} className="cdx-btn cdx-btn--ghost" style={{ padding: '15px 30px', fontSize: 17 }}>
                Ver demo
              </button>
            </div>

            {/* Trust indicators */}
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm" style={{ color: 'var(--cdx-ink-faint)' }}>
              {['Sin tarjeta de crédito', 'Sin publicidad', 'Datos seguros'].map((t) => (
                <span key={t} className="flex items-center gap-2">
                  <Sprig /> {t}
                </span>
              ))}
            </div>
          </motion.div>

          {/* ---- Right column: live phone demo (the real app, guest mode) ---- */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <PhoneDemo />

            {/* Floating colibrí */}
            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -top-6 -right-2 hidden md:block"
            >
              <BigColibri />
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 1.6, repeat: Infinity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[1]"
        style={{ color: 'var(--cdx-agave)' }}
      >
        <svg className="w-6 h-6" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
          <path d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </motion.div>
    </section>
  );
};

/* Small leaf bullet */
const Sprig = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M8 14C8 9 5 5 2 4c0 5 3 9 6 10Z" fill="#2E8B57" />
    <path d="M8 14C8 9 11 5 14 4c0 5-3 9-6 10Z" fill="#1F5C3D" />
  </svg>
);

/* Phone demo — the REAL app inside a phone frame (guest mode), following
 * the atmtruth landing pattern: blurred halo behind, 48px-radius frame with
 * a 10px ink border + subtle accent ring, pill notch, full-bleed iframe and
 * a caption link underneath. Same-origin iframe (/dashboard), interactive;
 * write actions inside it prompt for login via the guest-mode interceptor.
 * The iframe mounts after first paint so the landing never waits on it. */
const PhoneDemo = () => {
  const [ready, setReady] = React.useState(false);
  React.useEffect(() => {
    const id = window.requestAnimationFrame(() => setReady(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  return (
    <div className="relative mx-auto h-[610px] w-[min(310px,100%)] lg:h-[680px] lg:w-[345px]">
      {/* Halo behind the phone */}
      <div
        aria-hidden
        className="absolute -inset-12 rounded-full blur-3xl"
        style={{ background: 'rgba(31, 92, 61, 0.12)' }}
      />

      {/* Frame */}
      <div
        className="relative h-full overflow-hidden rounded-[48px] border-[10px]"
        style={{
          borderColor: '#16201A',
          background: 'var(--cdx-paper)',
          boxShadow:
            '0 0 0 1px rgba(31, 92, 61, 0.22), 0 18px 60px -28px rgba(31, 92, 61, 0.6), 0 35px 90px rgba(40, 30, 12, 0.22)',
        }}
      >
        {/* Pill notch */}
        <div
          aria-hidden
          className="absolute left-1/2 top-2 z-10 h-6 w-24 -translate-x-1/2 rounded-full"
          style={{ background: '#16201A' }}
        />
        {ready ? (
          <iframe
            src="/dashboard"
            title="Demo interactivo de Nutrition Intelligence (modo invitado)"
            loading="lazy"
            className="h-full w-full border-0"
            style={{ background: 'var(--cdx-paper)' }}
          />
        ) : (
          <div className="h-full w-full" style={{ background: 'var(--cdx-paper)' }} />
        )}
      </div>

      {/* Caption */}
      <p
        className="mt-4 text-center text-xs font-medium"
        style={{ color: 'var(--cdx-ink-faint)' }}
      >
        <span>La app real · demo en vivo</span>
        <span aria-hidden> · </span>
        <a
          href="/dashboard"
          style={{ color: 'var(--cdx-jade)', textDecoration: 'underline', textUnderlineOffset: 4 }}
        >
          Abrir la app completa
        </a>
      </p>
    </div>
  );
};

/* Larger colibrí for the hero (line-art, fluttering wing) */
const BigColibri = () => (
  <svg width="92" height="92" viewBox="0 0 96 96" fill="none" aria-hidden style={{ filter: 'drop-shadow(0 8px 16px rgba(30,22,8,0.25))' }}>
    <circle cx="48" cy="48" r="46" fill="#16201A" stroke="#C9A227" strokeWidth="1.5" />
    <path d="M38 62c-2-10 2-18 10-22 6-3 14-2 18 2l-6 4 8 0-4 6 6 2c-4 4-12 6-18 4" fill="#2E8B57" />
    <circle cx="37" cy="40" r="6.5" fill="#1F5C3D" />
    <path d="M31 38l-14-6 14 3z" fill="#C9A227" />
    <circle cx="35" cy="38.5" r="1.7" fill="#EBE2CF" />
    <g style={{ transformOrigin: '44px 44px', animation: 'cdx-flutter 0.45s ease-in-out infinite' }}>
      <path d="M44 44c8-8 18-10 26-6-6 2-10 6-12 12-6 0-12-2-14-6z" fill="#E0892E" opacity="0.94" />
    </g>
    <path d="M60 62l16 6-16 0z" fill="#8E2C42" />
  </svg>
);

export default Hero;
